import React, { useRef, useState, useEffect, Suspense } from "react"; 
import { Canvas, useFrame } from "@react-three/fiber";
import { Environment, MeshTransmissionMaterial, OrbitControls, useGLTF } from "@react-three/drei";

// Diamond model with responsive scaling
const DiamondModel = ({ isMobile }) => {
  const diamondRef = useRef();
  const { scene } = useGLTF("/pure_diamond.glb"); // Load your diamond model

  // Combined vertical float and rotation animation
  useFrame(({ clock }) => {
    if (diamondRef.current) {
      // Apply a gentle up and down movement
      const t = clock.getElapsedTime();
      diamondRef.current.position.y = Math.sin(t * 0.5) * 0.2 + (isMobile ? -1 : -1.5);
      
      // Add back the rotation around Y axis
      diamondRef.current.rotation.y += 0.01;
    }
  });

  // Adjust scale and position based on device size
  const scale = isMobile ? [4,4,4] : [3.5,3.5,3.5];
  const position = isMobile ? [0, -1, 0] : [0, -1.5, 0];

  return (
    <mesh ref={diamondRef} position={position} scale={scale}>
      <primitive object={scene} />
      <MeshTransmissionMaterial
        thickness={1}
        ior={2.4} 
        transmission={1} 
        chromaticAberration={0.02} 
        anisotropy={0.1} 
      />
    </mesh>
  );
};

const App = () => {
  // Responsive state management
  const [isMobile, setIsMobile] = useState(false);
  const [canvasHeight, setCanvasHeight] = useState('500px');
  const [isLoading, setIsLoading] = useState(true);
  
  // Check if device is mobile and adjust height based on screen size
  useEffect(() => {
    const handleResize = () => {
      // Detect if mobile
      setIsMobile(window.innerWidth < 768);
      
      // Responsive height calculation
      if (window.innerWidth < 480) {
        setCanvasHeight('300px');
      } else if (window.innerWidth < 768) {
        setCanvasHeight('400px');
      } else if (window.innerWidth < 1024) {
        setCanvasHeight('500px');
      } else {
        setCanvasHeight('600px');
      }
    };
    
    // Set initial size
    handleResize();
    
    // Add resize listener
    window.addEventListener('resize', handleResize);
    
    // Set a timer to hide the loading state after a few seconds
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000); // 3 seconds delay
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, []);

  // Fallback image for loading state
  const DiamondFallback = () => (
    <div className="diamond-fallback" style={{
      width: "100%",
      height: canvasHeight,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      background: "transparent"
    }}>
      <img 
        src="/media/diamond_transparent.svg" 
        alt="Diamond" 
        style={{
          maxWidth: "80%",
          maxHeight: "80%",
          objectFit: "contain",
          filter: "drop-shadow(0 0 15px rgba(0, 243, 255, 0.6))",
          backgroundColor: "transparent",
          animation: "float 6s ease-in-out infinite"
        }}
      />
      {isLoading && (
        <div style={{
          position: "absolute",
          bottom: "10%",
          left: "0",
          width: "100%",
          textAlign: "center",
          color: "rgba(255,255,255,0.7)",
          fontSize: "14px"
        }}>
          Loading 3D model...
        </div>
      )}
    </div>
  );

  return (
    <div className="diamond-viewer-container" style={{ width: "100%", maxWidth: "100vw", overflow: "hidden" }}>
      {isLoading ? (
        <DiamondFallback />
      ) : (
        <Canvas 
          camera={{ 
            position: isMobile ? [0, 0, 8] : [0, 0, 10], 
            fov: isMobile ? 70 : 60 
          }}
          style={{ 
            background: "transparent", 
            width: "100%", 
            height: canvasHeight,
            touchAction: "none" // Prevents touch events issues on mobile
          }}
          dpr={[1, 2]} // Optimizes pixel ratio for performance
        >
          {/* Lights for Reflection - adjusted for mobile */}
          <ambientLight intensity={0.7} />
          <directionalLight 
            position={isMobile ? [3, 8, 3] : [5, 10, 5]} 
            intensity={isMobile ? 1.5 : 2} 
          />
          <pointLight 
            position={isMobile ? [-3, -8, -3] : [-5, -10, -5]} 
            intensity={isMobile ? 1.5 : 2} 
          />

          {/* 3D Diamond with responsive props */}
          <Suspense fallback={null}>
            <DiamondModel isMobile={isMobile} />
          </Suspense>

          {/* Realistic Reflections */}
          <Environment preset="studio" />

          {/* Camera Controls with mobile options */}
          <OrbitControls 
            enableZoom={false}
            rotateSpeed={isMobile ? 0.7 : 1} 
            dampingFactor={0.1}
            enableDamping
          />
        </Canvas>
      )}
    </div>
  );
};

export default App;